import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { uploadCustomImage } from '../../api/api';
import {useToast} from '../../components/ToastProvider/ToastProvider';

const ImageUploader = ({ 
  stagedProductId, 
  onImageUploaded,
  onCancel 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentUploading, setCurrentUploading] = useState(null);

  const { showToast } = useToast();

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    // Check if adding these files would exceed the limit
    if (uploadedImages.length + acceptedFiles.length > 3) {
      showToast('Maximum 3 images allowed. Please remove some images first.', 'warning');
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    try {
      let lastResponse = null;
      // Upload files sequentially to avoid overwhelming the server
      for (const file of acceptedFiles) {
        setCurrentUploading(file.name);
        
        const response = await uploadCustomImage(stagedProductId, file);
        lastResponse = response; // Keep last response which has all image_urls
        
        // Backend returns image_url (single) and image_urls (array with all images)
        const imageUrl = response.image_url || (response.image_urls && response.image_urls[response.image_urls.length - 1]);
        const imageData = {
          url: imageUrl,
          filename: file.name
        };
        
        setUploadedImages(prev => [...prev, imageData]);
        uploadedUrls.push(imageUrl);
      }
      
      // Call the callback with the full response (contains image_urls array with all images)
      onImageUploaded(lastResponse || { image_urls: uploadedUrls });
      showToast(`${uploadedUrls.length} image(s) uploaded successfully!`, 'success');
    } catch (err) {
      console.error('Image upload error:', err);
      showToast('Failed to upload images. Please try again.', 'error'); 
    } finally {
      setUploading(false);
      setCurrentUploading(null);
    }
  }, [stagedProductId, onImageUploaded, showToast, uploadedImages.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 3,
    multiple: true,
    disabled: uploading
  });

  const handleRemoveImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setUploadedImages([]);
    onCancel();
    showToast('Image upload canceled.', 'info');
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Upload Custom Images (Max 3)
      </Typography>

      {uploadedImages.length === 0 && (
        <Card
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <input {...getInputProps()} />
            
            {uploading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress />
                <Typography>Uploading image...</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Typography variant="h6">
                  {isDragActive ? 'Drop the images here' : 'Drag & drop images here'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to select files
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supports: JPEG, PNG, GIF, WebP (Max 10MB each, Max 3 images)
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {uploadedImages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Uploaded Images ({uploadedImages.length}/3)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {uploadedImages.map((image, index) => (
              <Card key={index} sx={{ width: 200, position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="150"
                  image={image.url}
                  alt={`Uploaded image ${index + 1}`}
                  sx={{ objectFit: 'contain' }}
                />
                <CardContent sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <CheckIcon color="success" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" sx={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {image.filename}
                      </Typography>
                    </Box>
                    <Tooltip title="Remove image">
                      <IconButton onClick={() => handleRemoveImage(index)} size="small">
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {currentUploading && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ mb: 1 }} />
            <Typography variant="body2">
              Uploading: {currentUploading}
            </Typography>
          </CardContent>
        </Card>
      )}

      {uploadedImages.length > 0 && uploadedImages.length < 3 && (
        <Card
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <input {...getInputProps()} />
            <Typography variant="body2" color="text.secondary">
              {isDragActive ? 'Drop more images here' : 'Add more images'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {3 - uploadedImages.length} more image(s) allowed
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={uploading}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ImageUploader;
